import {
  searchContractManagementContractualParties,
  type ContractManagementContractualPartyLegalEntityInDb,
  type ContractManagementContractualPartyNaturalPersonInDb,
} from '@/api';
import type {
  GaiaRemoteFetch,
  GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import { ACCESS_PERMISSION_WRITE, deniedReason } from '@/utils/accessPermission';

/** A party as the API returns it, told apart by `kind`. */
export type ContractualParty =
  | ({ kind: 'natural_person' } & ContractManagementContractualPartyNaturalPersonInDb)
  | ({ kind: 'legal_entity' } & ContractManagementContractualPartyLegalEntityInDb);

/**
 * A party as the list and detail pages read it: its representative links —
 * a company's representatives, a person's represented companies — flattened
 * for display and the pickers. A person's companies aren't embedded (the
 * caller may not see them all), so only the detail page loads and passes them;
 * elsewhere `represented_uids` stays undefined, which tells an edit not to touch them.
 */
export type ContractualPartyRow = ContractualParty & {
  representative_uids: string[];
  representative_names: string;
  represented_uids?: string[];
  represented_names: string;
  represented_parties: ContractualParty[];
  default_representative_uid: string;
};

export const PARTY_KIND_LABELS: Record<string, string> = {
  legal_entity: 'Legal entity',
  natural_person: 'Natural person',
};

export function toPartyRow(
  party: ContractualParty,
  represented?: ContractualParty[]
): ContractualPartyRow {
  const representatives = party.kind === 'legal_entity' ? (party.representatives ?? []) : [];
  return {
    ...party,
    representative_uids: representatives.map(person => person.uid),
    representative_names: representatives.map(person => person.name).join(', '),
    represented_uids: represented?.map(entity => entity.uid),
    represented_names: (represented ?? []).map(entity => entity.name).join(', '),
    represented_parties: represented ?? [],
    default_representative_uid:
      party.kind === 'legal_entity' ? (party.default_representative?.uid ?? '') : '',
  };
}

/**
 * Who a legal entity signs through unless someone picks otherwise: its
 * default representative, or its only one. A person, or an entity with
 * several and no default, has none.
 */
export function preselectedRepresentative(
  party: ContractualParty
): ContractManagementContractualPartyNaturalPersonInDb | null {
  if (party.kind !== 'legal_entity') return null;
  const representatives = party.representatives ?? [];
  const uid = party.default_representative?.uid;
  return (
    representatives.find(person => person.uid === uid) ??
    (representatives.length === 1 ? representatives[0] : null)
  );
}

/**
 * A searched, paged picker over one kind of party — an org can hold any number.
 * `requireWrite` is for a pick that changes the picked party (a person chosen
 * under "Represents" becomes one of that company's representatives): parties
 * the caller can only read stay listed but disabled, with the reason beside
 * the name.
 */
export function searchPartiesOfKind(
  kind: 'natural_person' | 'legal_entity',
  { requireWrite = false }: { requireWrite?: boolean } = {}
): GaiaRemoteFetch {
  return async (text, page): Promise<GaiaRemotePage> => {
    const { data } = await searchContractManagementContractualParties({
      body: {
        text,
        limit: 20,
        offset: (page - 1) * 20,
        sort_key: 'name',
        sort_direction: 'asc',
        similarity_threshold: 0.5,
        column_filters: { kind: [kind] },
      },
      throwOnError: true,
    });
    return {
      options: data.results.map(party => {
        const reason = requireWrite
          ? deniedReason(party.permissions, ACCESS_PERMISSION_WRITE)
          : null;
        return reason
          ? { label: `${party.name} · ${reason}`, value: party.uid, disabled: true }
          : { label: party.name, value: party.uid };
      }),
      hasMore: page * 20 < data.result_count,
    };
  };
}
